/**
 * useUserPreferences.js
 * 사용자 환경설정을 Firestore에 저장하고 동기화하는 커스텀 훅
 * localStorage 대신 Firestore를 사용하여 모든 기기에서 동기화됨
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';

// 기본값 정의
const DEFAULT_DASHBOARD_PREFS = {
    todos: [],
    layouts: null,
    widgets: { students: true, attendance: true, birthday: true, budget: true, schedule: true, radar: true, goals: true, todo: true },
    bannerMessage: { type: 'default', text: '', source: '', x: 0, y: 60, isRightAligned: true },
    deptGoals: {}
};
const DASHBOARD_LOCAL_KEY_MAP = {
    todos: 'dashboard_todos',
    layouts: 'dashboard_layouts',
    widgets: 'dashboard_widgets',
    bannerMessage: 'dashboard_banner_message',
    deptGoals: 'dept_goals'
};

const DEFAULT_STATS_PREFS = {
    layout: null,
    widgets: {},
    deptInfo: {}
};

/**
 * 객체에서 undefined 값을 재귀적으로 제거 (Firestore는 undefined를 지원하지 않음)
 */
const removeUndefined = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
        return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
    }
    if (typeof obj === 'object') {
        const cleaned = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (value !== undefined) {
                cleaned[key] = removeUndefined(value);
            }
        });
        return cleaned;
    }
    return obj;
};

/**
 * 대시보드 환경설정 훅
 */
export function useDashboardPreferences() {
    const [prefs, setPrefs] = useState(DEFAULT_DASHBOARD_PREFS);
    const [isLoading, setIsLoading] = useState(true);
    const isInitialized = useRef(false);
    const saveTimeouts = useRef({});

    // Firestore 문서 경로
    const getDocRef = useCallback(() => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return null;
        return doc(db, 'users', user.uid, 'preferences', 'dashboard');
    }, []);

    // 초기 로드 (Firestore 또는 localStorage)
    useEffect(() => {
        const user = auth.currentUser;

        // 익명 사용자는 localStorage 사용
        if (!user || user.isAnonymous) {
            loadFromLocalStorage();
            setIsLoading(false);
            isInitialized.current = true;
            return;
        }

        const docRef = getDocRef();
        if (!docRef) {
            setIsLoading(false);
            return;
        }

        // Firestore에서 로드
        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setPrefs(prev => ({ ...prev, ...data }));
                } else {
                    // Firestore에 없으면 localStorage에서 마이그레이션
                    migrateFromLocalStorage();
                }
                setIsLoading(false);
                isInitialized.current = true;
            },
            (error) => {
                console.error('Error loading dashboard preferences:', error);
                loadFromLocalStorage();
                setIsLoading(false);
                isInitialized.current = true;
            }
        );

        return () => unsubscribe();
    }, [getDocRef]);

    useEffect(() => {
        return () => {
            Object.values(saveTimeouts.current).forEach((timeoutId) => clearTimeout(timeoutId));
        };
    }, []);

    // localStorage에서 로드
    const loadFromLocalStorage = () => {
        try {
            const todos = JSON.parse(localStorage.getItem('dashboard_todos') || '[]');
            const layouts = JSON.parse(localStorage.getItem('dashboard_layouts') || 'null');
            const widgets = JSON.parse(localStorage.getItem('dashboard_widgets') || 'null');
            const bannerMessage = JSON.parse(localStorage.getItem('dashboard_banner_message') || 'null');
            const deptGoals = JSON.parse(localStorage.getItem('dept_goals') || '{}');

            setPrefs({
                todos: todos || [],
                layouts: layouts,
                widgets: widgets || DEFAULT_DASHBOARD_PREFS.widgets,
                bannerMessage: bannerMessage || DEFAULT_DASHBOARD_PREFS.bannerMessage,
                deptGoals: deptGoals || {}
            });
        } catch (e) {
            console.error('Failed to load from localStorage:', e);
        }
    };

    // localStorage → Firestore 마이그레이션
    const migrateFromLocalStorage = async () => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return;

        const migratedData = {};
        try {
            const todos = localStorage.getItem('dashboard_todos');
            const layouts = localStorage.getItem('dashboard_layouts');
            const widgets = localStorage.getItem('dashboard_widgets');
            const bannerMessage = localStorage.getItem('dashboard_banner_message');
            const deptGoals = localStorage.getItem('dept_goals');

            if (todos) migratedData.todos = JSON.parse(todos);
            if (layouts) migratedData.layouts = JSON.parse(layouts);
            if (widgets) migratedData.widgets = JSON.parse(widgets);
            if (bannerMessage) migratedData.bannerMessage = JSON.parse(bannerMessage);
            if (deptGoals) migratedData.deptGoals = JSON.parse(deptGoals);

            if (Object.keys(migratedData).length > 0) {
                const docRef = getDocRef();
                if (docRef) {
                    await setDoc(docRef, removeUndefined(migratedData), { merge: true });
                    setPrefs(prev => ({ ...prev, ...migratedData }));
                    console.log('Migrated dashboard preferences to Firestore');
                }
            }
        } catch (e) {
            console.error('Failed to migrate to Firestore:', e);
            loadFromLocalStorage();
        }
    };

    // 업데이트 함수 (디바운스 적용)
    const updatePrefs = useCallback((key, value) => {
        // 로컬 상태 즉시 업데이트
        setPrefs(prev => ({ ...prev, [key]: value }));

        // 디바운스된 저장
        if (saveTimeouts.current[key]) clearTimeout(saveTimeouts.current[key]);
        saveTimeouts.current[key] = setTimeout(async () => {
            const user = auth.currentUser;

            // 익명 사용자는 localStorage에만 저장
            if (!user || user.isAnonymous) {
                const localKey = DASHBOARD_LOCAL_KEY_MAP[key];
                if (localKey) {
                    localStorage.setItem(localKey, JSON.stringify(value));
                }
                delete saveTimeouts.current[key];
                return;
            }

            // Firestore에 저장
            try {
                const docRef = doc(db, 'users', user.uid, 'preferences', 'dashboard');
                // undefined 값 제거 후 저장
                const cleanedValue = removeUndefined(value);
                if (cleanedValue !== null) {
                    await setDoc(docRef, { [key]: cleanedValue }, { merge: true });
                }
            } catch (e) {
                console.error('Failed to save to Firestore:', e);
                // Fallback: localStorage
                const localKey = DASHBOARD_LOCAL_KEY_MAP[key];
                if (localKey) {
                    localStorage.setItem(localKey, JSON.stringify(value));
                }
            }
            delete saveTimeouts.current[key];
        }, 500); // 500ms 디바운스
    }, []);

    // 여러 키를 한 번에 저장 (초기화 등 연속 업데이트 경쟁 상태 방지)
    const updatePrefsBatch = useCallback((patch) => {
        if (!patch || typeof patch !== 'object') return;

        setPrefs(prev => ({ ...prev, ...patch }));

        const patchKeys = Object.keys(patch);
        patchKeys.forEach((key) => {
            if (saveTimeouts.current[key]) clearTimeout(saveTimeouts.current[key]);
        });
        if (saveTimeouts.current.__batch__) clearTimeout(saveTimeouts.current.__batch__);

        saveTimeouts.current.__batch__ = setTimeout(async () => {
            const user = auth.currentUser;

            if (!user || user.isAnonymous) {
                patchKeys.forEach((key) => {
                    const localKey = DASHBOARD_LOCAL_KEY_MAP[key];
                    if (localKey) {
                        localStorage.setItem(localKey, JSON.stringify(patch[key]));
                    }
                });
                patchKeys.forEach((key) => delete saveTimeouts.current[key]);
                delete saveTimeouts.current.__batch__;
                return;
            }

            try {
                const docRef = doc(db, 'users', user.uid, 'preferences', 'dashboard');
                const cleanedPatch = removeUndefined(patch);
                if (cleanedPatch) {
                    await setDoc(docRef, cleanedPatch, { merge: true });
                }
            } catch (e) {
                console.error('Failed to batch save dashboard preferences:', e);
                patchKeys.forEach((key) => {
                    const localKey = DASHBOARD_LOCAL_KEY_MAP[key];
                    if (localKey) {
                        localStorage.setItem(localKey, JSON.stringify(patch[key]));
                    }
                });
            }

            patchKeys.forEach((key) => delete saveTimeouts.current[key]);
            delete saveTimeouts.current.__batch__;
        }, 500);
    }, []);

    // 전체 초기화
    const resetPrefs = useCallback(async () => {
        const defaultPrefs = DEFAULT_DASHBOARD_PREFS;
        setPrefs(defaultPrefs);

        const user = auth.currentUser;
        if (!user || user.isAnonymous) {
            localStorage.removeItem('dashboard_todos');
            localStorage.removeItem('dashboard_layouts');
            localStorage.removeItem('dashboard_widgets');
            localStorage.removeItem('dashboard_banner_message');
            localStorage.removeItem('dept_goals');
            return;
        }

        try {
            const docRef = doc(db, 'users', user.uid, 'preferences', 'dashboard');
            await setDoc(docRef, defaultPrefs);
        } catch (e) {
            console.error('Failed to reset preferences:', e);
        }
    }, []);

    return { prefs, updatePrefs, updatePrefsBatch, resetPrefs, isLoading };
}

/**
 * 통계 환경설정 훅
 */
export function useStatsPreferences() {
    const [prefs, setPrefs] = useState(DEFAULT_STATS_PREFS);
    const [isLoading, setIsLoading] = useState(true);
    const saveTimeout = useRef(null);

    // Firestore 문서 경로
    const getDocRef = useCallback(() => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return null;
        return doc(db, 'users', user.uid, 'preferences', 'statistics');
    }, []);

    // 초기 로드
    useEffect(() => {
        const user = auth.currentUser;

        if (!user || user.isAnonymous) {
            loadFromLocalStorage();
            setIsLoading(false);
            return;
        }

        const docRef = getDocRef();
        if (!docRef) {
            setIsLoading(false);
            return;
        }

        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    setPrefs(prev => ({ ...prev, ...docSnap.data() }));
                } else {
                    migrateFromLocalStorage();
                }
                setIsLoading(false);
            },
            (error) => {
                console.error('Error loading stats preferences:', error);
                loadFromLocalStorage();
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [getDocRef]);

    const loadFromLocalStorage = () => {
        try {
            const layout = JSON.parse(localStorage.getItem('stats_layout') || 'null');
            const widgets = JSON.parse(localStorage.getItem('stats_widgets') || '{}');
            const deptInfo = JSON.parse(localStorage.getItem('stats_dept_info') || '{}');

            setPrefs({
                layout: layout,
                widgets: widgets,
                deptInfo: deptInfo
            });
        } catch (e) {
            console.error('Failed to load from localStorage:', e);
        }
    };

    const migrateFromLocalStorage = async () => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return;

        const migratedData = {};
        try {
            const layout = localStorage.getItem('stats_layout');
            const widgets = localStorage.getItem('stats_widgets');
            const deptInfo = localStorage.getItem('stats_dept_info');

            if (layout) migratedData.layout = JSON.parse(layout);
            if (widgets) migratedData.widgets = JSON.parse(widgets);
            if (deptInfo) migratedData.deptInfo = JSON.parse(deptInfo);

            if (Object.keys(migratedData).length > 0) {
                const docRef = getDocRef();
                if (docRef) {
                    await setDoc(docRef, removeUndefined(migratedData), { merge: true });
                    setPrefs(prev => ({ ...prev, ...migratedData }));
                    console.log('Migrated stats preferences to Firestore');
                }
            }
        } catch (e) {
            console.error('Failed to migrate to Firestore:', e);
            loadFromLocalStorage();
        }
    };

    const updatePrefs = useCallback((key, value) => {
        setPrefs(prev => ({ ...prev, [key]: value }));

        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(async () => {
            const user = auth.currentUser;

            if (!user || user.isAnonymous) {
                const localKeyMap = {
                    layout: 'stats_layout',
                    widgets: 'stats_widgets',
                    deptInfo: 'stats_dept_info'
                };
                const localKey = localKeyMap[key];
                if (localKey) {
                    localStorage.setItem(localKey, JSON.stringify(value));
                }
                return;
            }

            try {
                const docRef = doc(db, 'users', user.uid, 'preferences', 'statistics');
                const cleanedValue = removeUndefined(value);
                if (cleanedValue !== null) {
                    await setDoc(docRef, { [key]: cleanedValue }, { merge: true });
                }
            } catch (e) {
                console.error('Failed to save to Firestore:', e);
            }
        }, 500);
    }, []);

    const resetPrefs = useCallback(async () => {
        setPrefs(DEFAULT_STATS_PREFS);

        const user = auth.currentUser;
        if (!user || user.isAnonymous) {
            localStorage.removeItem('stats_layout');
            localStorage.removeItem('stats_widgets');
            localStorage.removeItem('stats_dept_info');
            return;
        }

        try {
            const docRef = doc(db, 'users', user.uid, 'preferences', 'statistics');
            await setDoc(docRef, DEFAULT_STATS_PREFS);
        } catch (e) {
            console.error('Failed to reset preferences:', e);
        }
    }, []);

    return { prefs, updatePrefs, resetPrefs, isLoading };
}

/**
 * 부서 목표 공유 훅 (대시보드 + 사역통계 동기화)
 * 부서 비전/목표는 하나의 Firestore 경로에서 관리하여 양쪽에서 동기화됨
 */
export function useDeptGoals() {
    const [deptGoals, setDeptGoals] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const saveTimeout = useRef(null);

    // 공통 Firestore 경로: users/{uid}/preferences/shared
    const getDocRef = useCallback(() => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return null;
        return doc(db, 'users', user.uid, 'preferences', 'shared');
    }, []);

    useEffect(() => {
        const user = auth.currentUser;

        if (!user || user.isAnonymous) {
            // 익명 사용자는 localStorage 사용
            try {
                const saved = localStorage.getItem('shared_dept_goals');
                if (saved) setDeptGoals(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to load dept goals from localStorage:', e);
            }
            setIsLoading(false);
            return;
        }

        const docRef = getDocRef();
        if (!docRef) {
            setIsLoading(false);
            return;
        }

        // Firestore 실시간 구독
        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.deptGoals) {
                        setDeptGoals(data.deptGoals);
                    }
                } else {
                    // 데이터가 없으면 기존 localStorage에서 마이그레이션 시도
                    migrateFromLegacy();
                }
                setIsLoading(false);
            },
            (error) => {
                console.error('Error loading dept goals:', error);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [getDocRef]);

    // 기존 localStorage 또는 분리된 Firestore 데이터에서 마이그레이션
    const migrateFromLegacy = async () => {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return;

        try {
            // 1. localStorage에서 확인
            const localDeptGoals = localStorage.getItem('dept_goals');
            const localDeptInfo = localStorage.getItem('stats_dept_info');
            let migratedData = {};

            if (localDeptGoals) {
                migratedData = { ...migratedData, ...JSON.parse(localDeptGoals) };
            }
            if (localDeptInfo) {
                migratedData = { ...migratedData, ...JSON.parse(localDeptInfo) };
            }

            // 2. 기존 Firestore 경로에서 확인 (dashboard/deptGoals, statistics/deptInfo)
            const dashboardDoc = await getDoc(doc(db, 'users', user.uid, 'preferences', 'dashboard'));
            if (dashboardDoc.exists() && dashboardDoc.data().deptGoals) {
                migratedData = { ...migratedData, ...dashboardDoc.data().deptGoals };
            }

            const statsDoc = await getDoc(doc(db, 'users', user.uid, 'preferences', 'statistics'));
            if (statsDoc.exists() && statsDoc.data().deptInfo) {
                migratedData = { ...migratedData, ...statsDoc.data().deptInfo };
            }

            // 3. 통합 경로에 저장
            if (Object.keys(migratedData).length > 0) {
                const docRef = getDocRef();
                if (docRef) {
                    await setDoc(docRef, { deptGoals: removeUndefined(migratedData) }, { merge: true });
                    setDeptGoals(migratedData);
                    console.log('Migrated dept goals to shared location');
                }
            }
        } catch (e) {
            console.error('Failed to migrate dept goals:', e);
        }
    };

    // 업데이트 함수
    const updateDeptGoals = useCallback((newGoals) => {
        setDeptGoals(newGoals);

        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(async () => {
            const user = auth.currentUser;

            if (!user || user.isAnonymous) {
                localStorage.setItem('shared_dept_goals', JSON.stringify(newGoals));
                return;
            }

            try {
                const docRef = doc(db, 'users', user.uid, 'preferences', 'shared');
                await setDoc(docRef, { deptGoals: removeUndefined(newGoals) }, { merge: true });
            } catch (e) {
                console.error('Failed to save dept goals:', e);
                localStorage.setItem('shared_dept_goals', JSON.stringify(newGoals));
            }
        }, 500);
    }, []);

    return { deptGoals, updateDeptGoals, isLoading };
}


export default { useDashboardPreferences, useStatsPreferences, useDeptGoals };
